import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NativeSelect,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import type { DeletionCase, DeletionOutcome, DeletionStep } from "@/api/accountDeletion";
import { env } from "@/config/env";
import { Mono } from "@/components/Mono";
import { actionError, isFutureDate, reviewDescription, reviewTitle } from "./presentation";
import type { AccountDeletionDetailViewProps } from "./types";

interface CaseDialogProps {
  data: DeletionCase;
  expectedUpdatedAt: string;
  disabled: boolean;
  pending: boolean;
  onClose(): void;
}

/** Dialogs hold the reviewed version; a new live version requires reopening. */
export function ReviewDialog({
  data,
  step,
  expectedUpdatedAt,
  disabled,
  pending,
  onClose,
  onReview,
}: CaseDialogProps & {
  step: DeletionStep;
  onReview: AccountDeletionDetailViewProps["onReview"];
}) {
  const [outcome, setOutcome] = useState<DeletionOutcome | null>(null);
  const [reference, setReference] = useState("");
  const [policy, setPolicy] = useState("");
  const [until, setUntil] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = data.request.updatedAt !== expectedUpdatedAt;
  const completed = data.request.state === "completed";
  const purgesNow = step === "identityRecords" && completed;
  const finalNoticeBlocked =
    step === "receipt" && outcome === "erased" && !data.canRecordFinalNotice;
  const retentionValid =
    outcome !== "retained" || (policy.trim().length > 0 && isFutureDate(until));
  const outcomeAllowed =
    !!outcome &&
    ["ready", "erased", "retained"].includes(outcome) &&
    !(completed && outcome === "ready") &&
    !((step === "receipt" || step === "backups") && outcome === "retained");
  const busy = pending || submitting;
  const canSubmit =
    outcomeAllowed &&
    !!reference.trim() &&
    confirmed &&
    retentionValid &&
    !changed &&
    !disabled &&
    !finalNoticeBlocked &&
    !busy;
  const erasureLabel =
    step === "identityRecords"
      ? purgesNow
        ? "Delete retained local identity records now"
        : "Approve identity data deletion"
      : step === "receipt"
        ? "Final notice delivered"
        : step === "retention"
          ? "No data needs to be kept"
          : "Deletion verified";

  async function submit() {
    if (!canSubmit || !outcome) return;
    setSubmitting(true);
    setError(null);
    try {
      await onReview(
        {
          step,
          outcome,
          evidenceReference: reference.trim(),
          retentionPolicy: outcome === "retained" ? policy.trim() : null,
          retentionUntil: outcome === "retained" ? new Date(until).toISOString() : null,
        },
        expectedUpdatedAt,
      );
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened
      onClose={() => {
        if (!busy) onClose();
      }}
      title={reviewTitle(step)}
      centered
      size="lg"
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Stack>
          <Text size="sm">
            {purgesNow
              ? "Delete the retained local identity and terms records now, or record why they must still be kept."
              : reviewDescription(step)}
          </Text>
          {env.isProd ? <Alert color="red">This changes a production account.</Alert> : null}
          <NativeSelect
            label="Result"
            value={outcome ?? ""}
            onChange={(event) => {
              setOutcome((event.currentTarget.value || null) as DeletionOutcome | null);
              setConfirmed(false);
            }}
            data={[
              { value: "", label: "Choose a result", disabled: true },
              ...(completed
                ? []
                : [
                    {
                      value: "ready",
                      label:
                        step === "receipt"
                          ? "Delivery channel verified"
                          : "Ready to delete (work still pending)",
                    },
                  ]),
              {
                value: "erased",
                label: erasureLabel,
                disabled: step === "receipt" && !data.canRecordFinalNotice,
              },
              ...(step === "receipt" || step === "backups"
                ? []
                : [{ value: "retained", label: "Keep under an approved policy" }]),
            ]}
            disabled={busy}
            required
          />
          {step === "receipt" && !data.canRecordFinalNotice ? (
            <Text size="sm" c="dimmed">
              Wait for identity deletion and all cleanup checks before saving delivery.
            </Text>
          ) : null}
          <TextInput
            label="Case reference"
            description="Reference only. Keep personal data and credentials in the restricted case."
            placeholder="CASE-123 / review-4"
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            disabled={busy}
            maxLength={512}
            required
          />
          {outcome === "retained" ? (
            <>
              <TextInput
                label="Approved retention policy"
                value={policy}
                onChange={(e) => setPolicy(e.currentTarget.value)}
                disabled={busy}
                maxLength={128}
                required
              />
              <TextInput
                type="datetime-local"
                label="Keep until or review on"
                description="Choose a future date in your local time."
                value={until}
                onChange={(e) => setUntil(e.currentTarget.value)}
                disabled={busy}
                error={until && !isFutureDate(until) ? "Choose a future date." : undefined}
                required
              />
            </>
          ) : null}
          <Checkbox
            label={
              step === "identityRecords" && outcome === "erased"
                ? purgesNow
                  ? "I verified that retention has ended and approve deleting these local identity records now."
                  : "I verified the evidence and approve deleting local identity records after the final checks."
                : step === "receipt" && outcome === "erased"
                  ? "I verified delivery of the final notice, including what data is kept and for how long."
                  : "I verified this result and its supporting evidence."
            }
            checked={confirmed}
            onChange={(e) => setConfirmed(e.currentTarget.checked)}
            disabled={busy || !outcome}
          />
          {changed ? (
            <Alert color="yellow">
              This case changed. Close this dialog and check the latest details.
            </Alert>
          ) : null}
          {error ? (
            <Alert color="red" role="alert">
              {error}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              color={purgesNow && outcome === "erased" ? "red" : undefined}
              disabled={!canSubmit}
              loading={busy}
            >
              {purgesNow && outcome === "erased"
                ? "Delete identity records"
                : step === "receipt" && outcome === "erased"
                  ? "Save delivery"
                  : "Save review"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function FinalizeDialog({
  data,
  expectedUpdatedAt,
  disabled,
  pending,
  onClose,
  onFinalize,
}: CaseDialogProps & {
  onFinalize: AccountDeletionDetailViewProps["onFinalize"];
}) {
  const [accountId, setAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = data.request.updatedAt !== expectedUpdatedAt;
  const busy = pending || submitting;
  const canSubmit =
    data.canFinalize &&
    !changed &&
    !disabled &&
    !busy &&
    accountId.trim() === data.request.user &&
    !!reference.trim() &&
    confirmed;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onFinalize({ evidenceReference: reference.trim() }, expectedUpdatedAt);
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened
      onClose={() => {
        if (!busy) onClose();
      }}
      title="Start deletion"
      centered
      size="lg"
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Stack>
          <Alert color="red">
            This starts irreversible deletion. Account access will close; eligible identity data and
            recovery backups are deleted after the final checks.
          </Alert>
          {env.isProd ? (
            <Text fw={600} c="red">
              You are acting in production.
            </Text>
          ) : null}
          <Group gap="xs">
            <Text size="sm">Account</Text>
            <Mono value={data.request.user} />
          </Group>
          <TextInput
            label="Type the account ID to confirm"
            value={accountId}
            onChange={(e) => setAccountId(e.currentTarget.value)}
            disabled={busy}
            autoComplete="off"
            required
          />
          <TextInput
            label="Approval reference"
            description="Reference the approved deletion decision in the restricted case."
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            disabled={busy}
            maxLength={512}
            required
          />
          <Checkbox
            label="I reviewed the checks and approve deleting this account."
            checked={confirmed}
            onChange={(e) => setConfirmed(e.currentTarget.checked)}
            disabled={busy}
          />
          {changed ? (
            <Alert color="yellow">
              This case changed. Close the dialog and check the latest details before starting
              deletion.
            </Alert>
          ) : null}
          {!data.canFinalize ? (
            <Alert color="yellow">Finish the remaining checks before starting deletion.</Alert>
          ) : null}
          {error ? (
            <Alert color="red" role="alert">
              {error}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" color="red" disabled={!canSubmit} loading={busy}>
              Start deletion
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
